import { useSyncExternalStore } from 'react';
import Cookies from 'universal-cookie';

// The permission keys the user granted the AI, kept in the browser like the
// other session cookies (dropped at logout by delAllCookies):
// { always: ['lead:write', ...], chats: { [chatId]: ['email-inbox:read'] } }
// The backend caps them by the user's own permissions on every turn.
const COOKIE = 'aiPermissions';
const cookies = new Cookies();
const listeners = new Set();

const EMPTY = { always: [], chats: {} };
let snapshot = normalize(cookies.get(COOKIE));

function normalize(value) {
  if (!value || typeof value !== 'object') return EMPTY;
  const chats = {};
  Object.entries(value.chats || {}).forEach(([chatId, keys]) => {
    if (Array.isArray(keys) && keys.length) chats[chatId] = keys;
  });
  return {
    always: Array.isArray(value.always) ? value.always : [],
    chats,
  };
}

function write(next) {
  snapshot = normalize(next);
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  try {
    cookies.set(COOKIE, snapshot, { path: '/', expires });
  } catch {
    // cookie storage unavailable: the grant still applies for this page
  }
  listeners.forEach((listener) => listener());
}

const unique = (keys) => Array.from(new Set(keys));

export function readAiGrants() {
  return snapshot;
}

/** All keys granted for a chat: the "always" grants plus the chat's own. */
export function aiGrantsFor(chatId) {
  return unique([
    ...snapshot.always,
    ...(chatId ? snapshot.chats[chatId] || [] : []),
  ]);
}

/** Remember a key the user approved for one chat (chatId) or always (null). */
export function addAiGrant(key, chatId) {
  if (!key) return;
  if (chatId) {
    write({
      ...snapshot,
      chats: {
        ...snapshot.chats,
        [chatId]: unique([...(snapshot.chats[chatId] || []), key]),
      },
    });
  } else {
    write({ ...snapshot, always: unique([...snapshot.always, key]) });
  }
}

/**
 * Set a feature area to none / read / write for all chats. group.scopes
 * holds the grantable keys of the area (from the API).
 */
export function setAiGroupLevel(group, level) {
  const areaKeys = [...group.scopes.read, ...group.scopes.write];
  const keep = snapshot.always.filter((key) => !areaKeys.includes(key));
  const granted =
    level === 'write'
      ? areaKeys
      : level === 'read'
        ? group.scopes.read
        : [];
  write({ ...snapshot, always: unique([...keep, ...granted]) });
}

/** The level the "always" grants give a feature area. */
export function aiGroupLevel(group, grants = snapshot) {
  if (group.scopes.write.some((key) => grants.always.includes(key)))
    return 'write';
  if (group.scopes.read.some((key) => grants.always.includes(key)))
    return 'read';
  return 'none';
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Re-renders when the grants change (toggles, approval answers). */
export function useAiGrants() {
  return useSyncExternalStore(subscribe, readAiGrants);
}
